import { APP_INITIALIZER, LOCALE_ID, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import locale from '@angular/common/locales/fr';
import { BrowserModule, Title } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxWebstorageModule, SessionStorageService } from 'ngx-webstorage';
import dayjs from 'dayjs';
import { NgbDateAdapter, NgbDatepickerConfig } from '@ng-bootstrap/ng-bootstrap';

import './config/dayjs';
import { SharedModule } from 'app/shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { NgbDateDayjsAdapter } from './config/datepicker-adapter.config';
import { fontAwesomeIcons } from './config/font-awesome-icons.config';
import { ApplicationConfigService } from '../services/application-config.service';
import { httpInterceptorProviders } from './interceptors';
import { ErrorComponent } from './layouts/error/error.component';
import { PageRibbonComponent } from './layouts/profiles/page-ribbon.component';
import { HttpLoaderFactory, missingTranslationHandler } from './config/translation.config';
import { NavigationModule } from './layouts/navigation/navigation.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoggerConfig, LoggerModule, NgxLoggerLevel } from 'ngx-logger';
// TODO: import { SortablejsModule } from 'ngx-sortablejs';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { MainWithThemeComponent } from './own/main-with-theme/main-with-theme.component';
import { serviceProviders } from './own/service-providers';
import { MDBSpinningPreloader, ToastModule } from 'ng-uikit-pro-standard';
import { initServiceFactory } from '../services/init-service-factory';
import { InitService } from '../services/init.service';
import { environment } from '../environments/environment';
import { BaryCanonicalService } from '../services/bary-canonical.service';
import { UpdateService } from '../services/update.service';
import { NgxEchartsModule } from 'ngx-echarts';

@NgModule({
  imports: [
    BrowserModule,
    SharedModule,
    ToastModule.forRoot(),
    BrowserAnimationsModule,
    // jhipster-needle-angular-add-module JHipster will add new module here
    AppRoutingModule,
    // Set this to true to enable service worker (PWA)
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    HttpClientModule,
    NgxDocViewerModule,
    NgxWebstorageModule.forRoot({ prefix: 'jhi', separator: '-', caseSensitive: true }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useFactory: missingTranslationHandler,
      },
    }),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    NavigationModule,
    // TODO: SortablejsModule.forRoot({ animation: 150 }),
    // @PPR: Installation d'un logger pour éviter de passer par un console.
    LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
  ],
  providers: [
    MDBSpinningPreloader,
    Title,
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: NgbDateAdapter, useClass: NgbDateDayjsAdapter },
    {
      provide: APP_INITIALIZER,
      useFactory: initServiceFactory,
      deps: [InitService],
      multi: true,
    },
    LoggerConfig,
    DatePipe,
    httpInterceptorProviders,
    serviceProviders,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (updates: UpdateService) => () => updates.init(),
      deps: [UpdateService],
    },
  ],
  declarations: [ErrorComponent, PageRibbonComponent, MainWithThemeComponent],
  bootstrap: [MainWithThemeComponent],
  schemas: [NO_ERRORS_SCHEMA],
  exports: [ErrorComponent],
})
export class AppModule {
  constructor(
    applicationConfigService: ApplicationConfigService,
    iconLibrary: FaIconLibrary,
    dpConfig: NgbDatepickerConfig,
    translateService: TranslateService,
    sessionStorageService: SessionStorageService,
    canonicalService: BaryCanonicalService
  ) {
    applicationConfigService.setEndpointPrefix(environment.urlApi);
    registerLocaleData(locale);
    iconLibrary.addIcons(...fontAwesomeIcons);
    dpConfig.minDate = { year: dayjs().subtract(100, 'year').year(), month: 1, day: 1 };
    translateService.setDefaultLang('fr');
    // if user have changed language and navigates away from the application and back to the application then use previously choosed language
    const langKey = sessionStorageService.retrieve('locale') ?? 'fr';
    translateService.use(langKey);
    canonicalService.updateCanonicalUrl();
  }
}
